/**
 * CorrectDrawDialog — admin tool to fix a lottery draw that was entered with
 * the wrong numbers or wrong date/time. Atomically:
 *   1. Reverses every settlement against the old draw (claws back winners,
 *      resets losers, returns tickets to pending).
 *   2. Re-creates the draw with the corrected fields.
 *   3. Re-settles all (now-pending) tickets against the corrected numbers.
 *
 * Surfaces a preview (winners to revert + total clawback $) BEFORE the
 * admin commits, so there are no surprises.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertTriangle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DRAW_TIMES = ['morning', 'midday', 'evening', 'night'];

const CorrectDrawDialog = ({ open, onOpenChange, draw, onCorrected }) => {
    if (!draw) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <CorrectDrawContent
                key={draw.id}
                draw={draw}
                onOpenChange={onOpenChange}
                onCorrected={onCorrected}
            />
        </Dialog>
    );
};

const CorrectDrawContent = ({ draw, onOpenChange, onCorrected }) => {
    const [numbers, setNumbers] = useState([...(draw.winning_numbers || [])]);
    const [drawDate, setDrawDate] = useState(draw.draw_date || '');
    const [drawingTime, setDrawingTime] = useState(draw.drawing_time || '');
    const [reason, setReason] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/lottery/admin/draws/${draw.id}/correction-preview`,
                    { withCredentials: true });
                if (!cancelled) setPreview(data);
            } catch (e) {
                if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load preview');
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [draw.id]);

    const hasChanges = (
        JSON.stringify(numbers) !== JSON.stringify(draw.winning_numbers)
        || drawDate !== draw.draw_date
        || drawingTime !== draw.drawing_time
    );

    const handleSubmit = async () => {
        if (!hasChanges) { toast.error('No changes — edit at least one field'); return; }
        if (!reason.trim()) { toast.error('Reason is required'); return; }
        if (numbers.some(n => n === null || n === '')) {
            toast.error(`Pick exactly ${draw.pick_type} numbers`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/lottery/admin/draws/${draw.id}/correct`, {
                winning_numbers: numbers.map(n => Number(n)),
                draw_date: drawDate,
                drawing_time: drawingTime,
                reason: reason.trim(),
            }, { withCredentials: true });
            toast.success(
                `Corrected. Reverted ${data.reverted.winners_reverted} winners ($${data.reverted.total_clawed_back.toFixed(2)} clawed back) · ` +
                `Re-settled ${data.resettled.winners} new winners ($${data.resettled.total_paid.toFixed(2)} paid)`
            );
            onOpenChange(false);
            onCorrected?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Correction failed');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-lg"
                data-testid="correct-draw-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        Correct Draw — {draw.state} Pick {draw.pick_type}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Fix the winning numbers or the draw date/time. All tickets settled against this
                        draw will be automatically re-settled.
                    </DialogDescription>
                </DialogHeader>

                {/* Impact preview */}
                {loading ? (
                    <div className="flex items-center text-xs text-[#A0AAB2]">
                        <Loader2 className="w-3 h-3 animate-spin mr-2" /> Loading impact…
                    </div>
                ) : preview && (preview.winners_to_revert > 0 || preview.losers_to_revert > 0) ? (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs space-y-1"
                        data-testid="correction-impact-preview">
                        <p className="text-amber-400 font-bold uppercase tracking-wide text-[10px]">⚠ Impact preview</p>
                        <p className="text-white">
                            <span className="font-mono font-bold">{preview.winners_to_revert}</span> winning ticket{preview.winners_to_revert === 1 ? '' : 's'} will be reverted
                        </p>
                        <p className="text-white">
                            <span className="font-mono font-bold">${preview.total_clawback.toFixed(2)}</span> will be clawed back from player balances
                        </p>
                        <p className="text-white">
                            <span className="font-mono font-bold">{preview.losers_to_revert}</span> losing ticket{preview.losers_to_revert === 1 ? '' : 's'} will be re-evaluated
                        </p>
                    </div>
                ) : (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300"
                        data-testid="correction-impact-preview">
                        No tickets settled against this draw yet — safe to correct.
                    </div>
                )}

                {/* Number editor */}
                <div>
                    <Label className="text-xs uppercase tracking-wide text-[#A0AAB2] mb-2 block">
                        Winning Numbers
                    </Label>
                    <div className="flex gap-2">
                        {numbers.map((n, idx) => (
                            <select key={idx} value={n}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setNumbers(arr => arr.map((x, i) => i === idx ? (v === '' ? '' : Number(v)) : x));
                                }}
                                className="w-12 h-12 rounded-lg bg-[#0a0a0a] border-2 border-[#FFD700]/30 text-[#FFD700] text-lg font-black text-center appearance-none"
                                data-testid={`correct-num-${idx}`}>
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        ))}
                        <div className="flex items-center text-xs text-[#A0AAB2] ml-2">
                            <span className="mr-2 font-mono">{(draw.winning_numbers || []).join('-')}</span>
                            <ArrowRight className="w-3 h-3 text-amber-400" />
                            <span className="ml-2 font-mono text-white font-bold">{numbers.join('-')}</span>
                        </div>
                    </div>
                </div>

                {/* Date + time */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-[#A0AAB2] mb-1 block">
                            Draw Date
                        </Label>
                        <Input type="date" value={drawDate}
                            onChange={(e) => setDrawDate(e.target.value)}
                            className="bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="correct-draw-date" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-[#A0AAB2] mb-1 block">
                            Drawing Time
                        </Label>
                        <select value={drawingTime}
                            onChange={(e) => setDrawingTime(e.target.value)}
                            className="w-full h-10 rounded-md bg-[#0a0a0a] border border-white/10 text-white px-3 capitalize"
                            data-testid="correct-drawing-time">
                            {DRAW_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                {/* Reason — REQUIRED */}
                <div>
                    <Label className="text-xs uppercase tracking-wide text-[#A0AAB2] mb-1 block">
                        Reason <span className="text-rose-400">*</span>
                    </Label>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. typed wrong numbers / wrong drawing slot"
                        className="bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="correct-reason" />
                    <p className="text-[10px] text-[#A0AAB2] mt-1">Required — saved to the audit log.</p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        className="border-white/10 text-white"
                        data-testid="correct-cancel">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}
                        disabled={submitting || !hasChanges || !reason.trim()}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                        data-testid="correct-submit">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Correction'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CorrectDrawDialog;
